// @ts-nocheck
/*
 * Port of views/dashboard/ivr/IVR/nodes/VoicemailNode.js (399 lines).
 * BYTE-IDENTICAL — reactflow → @xyflow/react, AudioPreviewModel → AudioPreviewModal.
 * Preserves: VM 5-60s clamp, silence-stop 0-10, stop-DTMF regex,
 * use_media toggle, hasLangParent language-set auto-fill, gather-parent
 * handle-cleanup on remove.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Button, Form } from './_rbShim';
import { Handle, useReactFlow } from '@xyflow/react';
import { NodesContext } from '../provider/IvrContext';
import AudioPreviewModal from '../Helpers/AudioPreviewModal';
import { toast } from 'sonner';
import {
  Voicemail as VoicemailIcon,
  Settings,
  Play,
  Trash2,
  Music,
} from 'lucide-react';
import IvrDrawer from '../Helpers/IvrDrawer';
import { triggerHandleConnect, findUpstreamLanguageNode } from '../Helpers/utils';
import {
  BACKEND_API_BASE_URL,
  VOICEMAIL_MAX_DURATION,
  VOICEMAIL_MIN_DURATION,
} from '@/utilities/constant';
import { getApiCall } from '@/service/httpCall';

const VoicemailNode = ({ data, id }: any) => {
  const { setNodes, getNodes, getEdges } = useReactFlow();
  const { updateNodeData, onRemove } = useContext(NodesContext) as any;

  const action = data.action || {};
  const maxDuration = data.max_duration || '';
  const promptCategory = action.prompt_category || '';
  const audioUrl = action.audio || action.languages?.[0]?.url || '';
  const audioName = action.name || '';
  const recordSilenceStop = data.recordSilenceStop ?? 0;
  const recordStopDtmf = data.recordStopDtmf ?? 'any_dtmf';

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [playModalOpen, setPlayModalOpen] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [promptList, setPromptList] = useState<any[]>([]);

  const [answerLangCodes, setAnswerLangCodes] = useState<any[]>([]);
  const [hasLangParent, setHasLangParent] = useState(false);
  const [draftAction, setDraftAction] = useState<any>(action);
  const [draftLanguage, setDraftLanguage] = useState(action.language || '');
  const [draftDuration, setDraftDuration] = useState(maxDuration);
  const [draftCategory, setDraftCategory] = useState('general');
  const [draftUseMedia, setDraftUseMedia] = useState(Boolean(data.use_media));
  const [draftSilenceStop, setDraftSilenceStop] = useState<any>(recordSilenceStop);
  const [draftStopDtmf, setDraftStopDtmf] = useState<any>(recordStopDtmf);
  const [drawerPreview, setDrawerPreview] = useState<any>({
    open: false,
    url: '',
    name: '',
  });

  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const resp: any = await getApiCall(
          `${BACKEND_API_BASE_URL}/v2/prompt?user_id=${data.userId}`,
        );
        if (!Array.isArray(resp)) return;
        setPromptList(
          resp.filter(
            (p: any) =>
              p.prompt_category === 'general' &&
              Array.isArray(p.prompt_url) &&
              p.prompt_url.some((e: any) => e.status === 1),
          ),
        );
      } catch (err) {
        console.error(err);
      }
    };
    if (data.userId) fetchPrompts();
  }, [data.userId]);

  const updateNode = (updates: any) => updateNodeData(id, updates);

  const openModal = () => {
    const _answerCodes =
      getNodes().find((n: any) => n.type === 'answerNode')?.data?.languages ||
      [];
    const _langNode = findUpstreamLanguageNode(id, getNodes, getEdges);
    const _langCodes =
      _langNode &&
      Array.isArray(_langNode.data?.selectedLanguages) &&
      _langNode.data.selectedLanguages.length > 0
        ? _langNode.data.selectedLanguages
        : null;
    setHasLangParent(!!_langNode);
    setAnswerLangCodes(_langCodes ?? _answerCodes);
    setDraftAction(action);
    setDraftLanguage(action.language || '');
    setDraftDuration(maxDuration);
    setDraftCategory('general');
    setDraftUseMedia(Boolean(data.use_media));
    setDraftSilenceStop(recordSilenceStop);
    setDraftStopDtmf(recordStopDtmf);
    setErrors({});
    setModalIsOpen(true);
  };

  const handleSilenceStopChange = (e: any) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && Number(value) <= 10)) {
      setDraftSilenceStop(value);
    }
  };

  const handleStopDtmfChange = (e: any) => {
    setDraftStopDtmf(e.target.value);
  };

  const handleUseMediaToggle = (e: any) => {
    const checked = e.target.checked;
    setDraftUseMedia(checked);
    if (!checked) {
      setDraftCategory('');
      setDraftAction({});
    }
  };

  const closeModal = () => {
    setErrors({});
    setModalIsOpen(false);
  };

  const handleAudioChange = (e: any) => {
    const selected = promptList.find(
      (a: any) => a.id.toString() === e.target.value,
    );
    if (!selected) {
      setDraftAction((prev: any) => ({
        ...prev,
        id: '',
        audio: '',
        name: '',
        duration: 0,
      }));
      setDraftLanguage('');
      return;
    }
    const approvedLangs = Array.isArray(selected.prompt_url)
      ? selected.prompt_url.filter(
          (en: any) =>
            en.status === 1 &&
            (answerLangCodes.length === 0 ||
              answerLangCodes.includes(en.language)),
        )
      : [];

    if (hasLangParent) {
      setDraftAction({
        type: 'voicemail',
        prompt_category: 'general',
        id: selected.id,
        name: selected.prompt_name,
        duration: selected.prompt_duration || 0,
        languages: approvedLangs
          .filter((en: any) => en.status === 1)
          .map((en: any) => ({
            language: en.language,
            url: en.url,
            duration: en.duration ?? selected.prompt_duration ?? 0,
            status: en.status,
          })),
      });
      setDraftLanguage('');
      return;
    }

    const autoLang = approvedLangs.length === 1 ? approvedLangs[0].language : '';
    const autoAudio = approvedLangs.length === 1 ? approvedLangs[0].url : '';
    setDraftAction({
      type: 'voicemail',
      prompt_category: 'general',
      id: selected.id,
      audio: autoAudio,
      duration: selected.prompt_duration || 0,
      name: selected.prompt_name,
      language: autoLang,
    });
    setDraftLanguage(autoLang);
  };

  const handleLanguageChange = (e: any) => {
    const lang = e.target.value;
    setDraftLanguage(lang);
    const sel = promptList.find(
      (a: any) => a.id?.toString() === draftAction?.id?.toString(),
    );
    const entry =
      lang && sel && Array.isArray(sel.prompt_url)
        ? sel.prompt_url.find((en: any) => en.language === lang)
        : null;
    setDraftAction((prev: any) => ({
      ...prev,
      audio: entry?.url || '',
      language: lang,
    }));
  };

  const handleDurationChange = (e: any) => {
    const val = e.target.value;
    if (
      /^\d*$/.test(val) &&
      (val === '' || Number(val) <= VOICEMAIL_MAX_DURATION)
    ) {
      setDraftDuration(val);
    }
  };

  const validate = () => {
    const newErrors: any = {};
    if (draftUseMedia) {
      if (draftCategory && !draftAction?.id) {
        newErrors.audio = 'Audio is required when category is selected';
      }
      if (!hasLangParent && draftAction?.id && !draftLanguage)
        newErrors.language = 'Please select a language';
    }
    if (!draftDuration || Number(draftDuration) < VOICEMAIL_MIN_DURATION) {
      newErrors.duration = `Min ${VOICEMAIL_MIN_DURATION}s`;
    } else if (Number(draftDuration) > VOICEMAIL_MAX_DURATION) {
      newErrors.duration = `Max ${VOICEMAIL_MAX_DURATION}s`;
    }
    if (
      draftSilenceStop === '' ||
      draftSilenceStop === null ||
      draftSilenceStop === undefined
    ) {
      newErrors.recordSilenceStop = 'Silence stop is required';
    } else if (
      !/^\d+$/.test(String(draftSilenceStop)) ||
      Number(draftSilenceStop) < 0 ||
      Number(draftSilenceStop) > 10
    ) {
      newErrors.recordSilenceStop = 'Enter an integer from 0 to 10';
    }
    if (
      draftStopDtmf === '' ||
      draftStopDtmf === null ||
      draftStopDtmf === undefined
    ) {
      newErrors.recordStopDtmf = 'Stop DTMF is required';
    } else if (!/^(any_dtmf|\*|#)$/.test(String(draftStopDtmf))) {
      newErrors.recordStopDtmf = 'Must be Any DTMF, * or #';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    updateNode({
      action: draftUseMedia ? draftAction : {},
      max_duration: Number(draftDuration),
      use_media: draftUseMedia,
      recordSilenceStop: Number(draftSilenceStop),
      recordStopDtmf: String(draftStopDtmf),
    });
    toast.success('Voicemail saved.');
    setModalIsOpen(false);
  };

  const displayPromptList =
    answerLangCodes.length === 0
      ? promptList
      : promptList.filter(
          (p: any) =>
            Array.isArray(p.prompt_url) &&
            p.prompt_url.some(
              (e: any) =>
                e.status === 1 && answerLangCodes.includes(e.language),
            ),
        );

  const handleRemoveNode = () => {
    if (data.parent === 'gather Node' && data.idToRemove) {
      setNodes((nodes: any[]) =>
        nodes.map((node: any) => {
          if (!node.data.selectedHandles) return node;
          const updatedHandles = { ...node.data.selectedHandles };
          const updatedSmsData = { ...node.data.smsData };
          delete updatedHandles[data.idToRemove];
          if (updatedSmsData) {
            const parts = data.idToRemove.split('-');
            const handleIndex = parts[parts.length - 1];
            if (handleIndex) {
              const smsKey = `DTMF_${handleIndex}`;
              delete updatedSmsData[smsKey];
            }
          }
          return {
            ...node,
            data: {
              ...node.data,
              selectedHandles: updatedHandles,
              smsData: updatedSmsData,
            },
          };
        }),
      );
    }
    onRemove(id);
  };

  const renderListenBtn = (url: any, name: any) =>
    url ? (
      <button
        type="button"
        className="ivr-drawer-play-btn"
        title="Listen"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() =>
          setDrawerPreview({ open: true, url, name: name || 'Audio' })
        }
        style={{ marginLeft: 8 }}
      >
        ▶
      </button>
    ) : null;

  return (
    <div className="ivrn" style={{ '--node-color': '#4f46e5' } as any}>
      <span
        className="ivrn-icon"
        style={{ background: '#4f46e5', color: '#fff' }}
        onClick={triggerHandleConnect}
      >
        <VoicemailIcon size={22} strokeWidth={2} />
      </span>
      <Handle
        type="target"
        position={'left' as any}
        style={{ background: '#4f46e5', top: '65%' }}
      />

      <div className="ivrn-head">
        <span className="ivrn-title">Voicemail</span>
      </div>
      <div className="ivrn-body">
        {audioName ? (
          <span className="ivrn-val">
            <Music size={10} style={{ marginRight: 4, flexShrink: 0 }} />
            {audioName}
          </span>
        ) : (
          <span className="ivrn-hint">Click ⚙ to configure</span>
        )}
      </div>

      <div className="ivrn-actions">
        <button
          className="ivrn-btn ivrn-btn--cfg"
          title="Configure"
          onClick={openModal}
        >
          <Settings size={14} strokeWidth={2} />
        </button>
        {audioUrl && (
          <button
            className="ivrn-btn ivrn-btn--play"
            title="Preview"
            onClick={() => setPlayModalOpen(true)}
          >
            <Play size={14} strokeWidth={2} />
          </button>
        )}
        <button
          className="ivrn-btn ivrn-btn--del"
          title="Remove"
          onClick={handleRemoveNode}
        >
          <Trash2 size={14} strokeWidth={2} />
        </button>
      </div>

      <IvrDrawer
        show={modalIsOpen}
        onHide={closeModal}
        title="Voicemail"
        subtitle="Configure voicemail recording duration and optional greeting."
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              Close
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </>
        }
      >
        <Form>
          <Form.Group>
            <Form.Label>
              Max Voicemail Duration (sec, {VOICEMAIL_MIN_DURATION}-
              {VOICEMAIL_MAX_DURATION})
              <Form.Text className="text-danger">*</Form.Text>
            </Form.Label>
            <Form.Control
              type="number"
              min={VOICEMAIL_MIN_DURATION}
              max={VOICEMAIL_MAX_DURATION}
              value={draftDuration}
              onChange={handleDurationChange}
              placeholder={`Enter duration in seconds (min ${VOICEMAIL_MIN_DURATION})`}
            />
            {errors.duration && (
              <Form.Text className="text-danger">{errors.duration}</Form.Text>
            )}
          </Form.Group>

          <Form.Group style={{ marginTop: 15 }}>
            <Form.Label>
              Silence Stop (0 - 10 seconds)
              <Form.Text className="text-danger"> *</Form.Text>
            </Form.Label>
            <Form.Control
              type="number"
              min="0"
              max="10"
              step="1"
              value={draftSilenceStop}
              onChange={handleSilenceStopChange}
              placeholder="Enter an integer from 0 to 10"
            />
            {errors.recordSilenceStop && (
              <Form.Text className="text-danger">
                {errors.recordSilenceStop}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group style={{ marginTop: 15 }}>
            <Form.Label>
              Stop DTMF<Form.Text className="text-danger"> *</Form.Text>
            </Form.Label>
            <Form.Control
              as="select"
              value={draftStopDtmf}
              onChange={handleStopDtmfChange}
            >
              <option value="any_dtmf">Any DTMF (0 - 9)</option>
              <option value="*">*</option>
              <option value="#">#</option>
            </Form.Control>
            {errors.recordStopDtmf && (
              <Form.Text className="text-danger">
                {errors.recordStopDtmf}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group style={{ marginTop: 15 }}>
            <Form.Check
              type="checkbox"
              id={`vm-use-media-${id}`}
              label="Use Media"
              checked={draftUseMedia}
              onChange={handleUseMediaToggle}
            />
          </Form.Group>

          {draftUseMedia && (
            <>
              <Form.Group style={{ marginTop: 15 }}>
                <Form.Label>Select Audio</Form.Label>
                <Form.Control
                  as="select"
                  value={draftAction?.id || ''}
                  onChange={handleAudioChange}
                >
                  <option value="">Select Audio</option>
                  {displayPromptList.map((item: any) => (
                    <option key={item.id} value={item.id}>
                      {item.prompt_name}
                    </option>
                  ))}
                </Form.Control>
                {errors.audio && (
                  <Form.Text className="text-danger">{errors.audio}</Form.Text>
                )}
              </Form.Group>
              {hasLangParent ? (
                Array.isArray(draftAction?.languages) &&
                draftAction.languages.length > 0 && (
                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 12,
                      color: '#0f766e',
                      background: '#f0fdfa',
                      border: '1px solid #99f6e4',
                      borderRadius: 8,
                      padding: '8px 12px',
                    }}
                  >
                    This node is downstream of a Language node — all{' '}
                    {draftAction.languages.length} approved language
                    {draftAction.languages.length !== 1 ? 's' : ''} (
                    {draftAction.languages
                      .map((l: any) => l.language?.toUpperCase())
                      .join(', ')}
                    ) will be saved automatically.
                  </div>
                )
              ) : (
                (() => {
                  const sel = promptList.find(
                    (a: any) =>
                      a.id?.toString() === draftAction?.id?.toString(),
                  );
                  const langs = Array.isArray(sel?.prompt_url)
                    ? sel.prompt_url.filter(
                        (e: any) =>
                          e.status === 1 &&
                          (answerLangCodes.length === 0 ||
                            answerLangCodes.includes(e.language)),
                      )
                    : [];
                  if (!sel || langs.length === 0) return null;
                  return (
                    <Form.Group style={{ marginTop: 10 }}>
                      <Form.Label>
                        Select Audio Language{' '}
                        <span style={{ color: 'red' }}>*</span>
                      </Form.Label>
                      <Form.Control
                        as="select"
                        value={draftLanguage}
                        onChange={handleLanguageChange}
                      >
                        <option value="">Select Language</option>
                        {langs.map((e: any) => (
                          <option key={e.language} value={e.language}>
                            {e.language?.toUpperCase()}
                            {e.name ? ` — ${e.name}` : ''}
                          </option>
                        ))}
                      </Form.Control>
                      {errors.language && (
                        <Form.Text className="text-danger">
                          {errors.language}
                        </Form.Text>
                      )}
                    </Form.Group>
                  );
                })()
              )}
              {renderListenBtn(
                draftAction?.audio ||
                  draftAction?.languages?.[0]?.url ||
                  '',
                draftAction?.name,
              )}
            </>
          )}
        </Form>
      </IvrDrawer>

      <AudioPreviewModal
        show={playModalOpen}
        onClose={() => setPlayModalOpen(false)}
        audioUrl={audioUrl}
        title={audioName}
      />

      <AudioPreviewModal
        show={drawerPreview.open}
        onClose={() =>
          setDrawerPreview({ open: false, url: '', name: '' })
        }
        audioUrl={drawerPreview.url}
        title={drawerPreview.name}
      />
    </div>
  );
};

export default VoicemailNode;
